import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { KpiCard, Loader } from "@/components/common";
import { formatINR, formatDate, timeAgo } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Gem, Building2, Users, Zap, IndianRupee, Receipt, Package, UserCircle,
  Moon, Sun, LogOut, Search, TrendingUp, Crown, KeyRound, Copy, X,
  MessageCircle, Smartphone, CheckCircle2, PauseCircle, PlayCircle, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-lg text-xs">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="capitalize text-muted-foreground">{p.name}:</span>
          <span className="font-semibold">{p.name === "sales" ? formatINR(p.value, { compact: true }) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function FounderDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [data, setData] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [pending, setPending] = useState([]);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    setError(false);
    api.get("/founder/overview").then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/founder/businesses").then((r) => setBusinesses(r.data)).catch(() => setError(true));
    api.get("/founder/pending-signups").then((r) => setPending(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.get("/founder/pending-signups").then((r) => setPending(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => toast.success(`Code ${code} copied`)).catch(() => {});
  };
  const dismiss = async (email) => {
    try { await api.delete(`/founder/pending-signups/${encodeURIComponent(email)}`); setPending((p) => p.filter((x) => x.email !== email)); }
    catch { toast.error("Could not dismiss"); }
  };
  const approve = async (email, name) => {
    try {
      await api.post(`/founder/pending-signups/${encodeURIComponent(email)}/approve`);
      toast.success(`${name} approved — they can log in with their email & password now`);
      setPending((p) => p.filter((x) => x.email !== email));
      api.get("/founder/overview").then((r) => setData(r.data)).catch(() => {});
      api.get("/founder/businesses").then((r) => setBusinesses(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const removeShop = async (b) => {
    if (!window.confirm(`Remove "${b.name}" and ALL its data permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/founder/businesses/${b.business_id}`);
      toast.success(`${b.name} removed`);
      setBusinesses((list) => list.filter((x) => x.business_id !== b.business_id));
      api.get("/founder/overview").then((r) => setData(r.data)).catch(() => {});
    } catch { toast.error("Could not remove shop"); }
  };
  const suspendShop = async (b) => {
    const next = !b.is_suspended;
    if (next && !window.confirm(`Suspend "${b.name}"? Their login will be frozen (data is kept). You can reactivate anytime.`)) return;
    try {
      await api.post(`/founder/businesses/${b.business_id}/suspend?suspend=${next}`);
      toast.success(next ? `${b.name} suspended — login frozen` : `${b.name} reactivated`);
      setBusinesses((list) => list.map((x) => (x.business_id === b.business_id ? { ...x, is_suspended: next } : x)));
    } catch { toast.error("Could not update shop"); }
  };
  const cleanupTest = async () => {
    if (!window.confirm("Remove ALL test/QA shops (names starting with TEST or QA)? The demo and real shops are kept. This cannot be undone.")) return;
    try {
      const res = await api.post("/founder/cleanup-test");
      toast.success(`${res.data.removed_count} test shop(s) removed`);
      load();
    } catch { toast.error("Cleanup failed"); }
  };
  const codeMsg = (p) => `Hi ${p.name}, your KHEVORA verification code is: ${p.code}\n\nOpen ${window.location.origin}/login → "Create account" with your email, then enter this code to log in.`;
  const waLink = (p) => `https://wa.me/${(p.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(codeMsg(p))}`;
  const smsLink = (p) => `sms:${p.phone}?&body=${encodeURIComponent(codeMsg(p))}`;

  const filtered = businesses.filter((b) =>
    `${b.name} ${b.owner_name} ${b.owner_email} ${b.owner_phone || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-30 h-16 flex items-center gap-3 border-b border-border glass px-4 md:px-8">
        <div className="flex items-center gap-2.5">
          <div className="grid place-items-center h-9 w-9 rounded-lg bg-primary text-primary-foreground">
            <Gem className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold tracking-tight">KHEVORA</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-primary -mt-0.5 flex items-center gap-1">
              <Crown className="h-2.5 w-2.5" /> Founder Console
            </div>
          </div>
        </div>
        <div className="flex-1" />
        <span className="hidden sm:block text-sm text-muted-foreground">{user?.name}</span>
        <button data-testid="founder-theme-toggle" onClick={toggle} className="grid place-items-center h-9 w-9 rounded-lg border border-border hover:bg-secondary transition-colors">
          {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>
        <button data-testid="founder-logout" onClick={logout} className="grid place-items-center h-9 w-9 rounded-lg border border-border hover:bg-secondary hover:text-destructive transition-colors">
          <LogOut className="h-[18px] w-[18px]" />
        </button>
      </header>

      <main className="p-4 md:p-8 max-w-[1500px] mx-auto">
        {error && !data ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="font-medium">Couldn't load platform data.</p>
            <p className="text-sm text-muted-foreground mt-1">Please check your connection and try again.</p>
            <button onClick={load} data-testid="founder-retry" className="mt-4 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold">Retry</button>
          </div>
        ) : !data ? <Loader label="Loading the platform…" /> : (
          <>
            <div className="mb-8 animate-fade-up">
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Platform Overview</h1>
              <p className="text-muted-foreground mt-1.5">Everything happening across KHEVORA — all businesses, live.</p>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KpiCard testid="f-businesses" index={0} label="Total Businesses" value={data.kpis.total_businesses} icon={Building2} accent="primary" />
              <KpiCard testid="f-active" index={1} label="Active (30d)" value={data.kpis.active_businesses} icon={Zap} accent="success" />
              <KpiCard testid="f-new-week" index={2} label="New This Week" value={data.kpis.new_week} icon={TrendingUp} accent="warning" />
              <KpiCard testid="f-sales" index={3} label="Platform Sales" value={formatINR(data.kpis.platform_sales, { compact: true })} icon={IndianRupee} accent="success" />
              <KpiCard testid="f-users" index={4} label="Total Users" value={data.kpis.total_users} icon={Users} accent="primary" />
              <KpiCard testid="f-invoices" index={5} label="Invoices" value={data.kpis.total_invoices} icon={Receipt} accent="primary" />
              <KpiCard testid="f-products" index={6} label="Products" value={data.kpis.total_products} icon={Package} accent="warning" />
              <KpiCard testid="f-customers" index={7} label="Customers" value={data.kpis.total_customers} icon={UserCircle} accent="primary" />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <div className="rounded-xl border border-border bg-card p-5 md:p-6 shadow-sm animate-fade-up">
                <h3 className="font-heading text-lg font-semibold mb-5">New Signups <span className="text-muted-foreground text-sm font-normal">· last 14 days</span></h3>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={data.signups_daily} margin={{ left: -20, right: 8, top: 6 }}>
                    <defs><linearGradient id="fg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} />
                    <Area type="monotone" dataKey="signups" stroke="#6366F1" strokeWidth={2.5} fill="url(#fg)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-xl border border-border bg-card p-5 md:p-6 shadow-sm animate-fade-up">
                <h3 className="font-heading text-lg font-semibold mb-5">Growth <span className="text-muted-foreground text-sm font-normal">· signups & sales / month</span></h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.signups_monthly} margin={{ left: -20, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                    <Bar dataKey="signups" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Verification requests (manual OTP) */}
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 md:p-6 shadow-sm mb-6 animate-fade-up" data-testid="verification-requests">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-primary" />
                  <h3 className="font-heading text-lg font-semibold">Verification Requests</h3>
                  {pending.length > 0 && <span className="text-xs font-bold bg-primary text-primary-foreground rounded-full px-2 py-0.5">{pending.filter((p) => !p.expired).length}</span>}
                </div>
                <span className="text-xs text-muted-foreground">Share the code with the person so they can log in</span>
              </div>
              {pending.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No pending signups right now. New signup codes appear here automatically.</p>
              ) : (
                <div className="space-y-2.5">
                  {pending.map((p) => (
                    <div key={p.email} className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border bg-card p-3 ${p.expired ? "border-border opacity-60" : "border-primary/30"}`} data-testid={`pending-${p.email}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{p.name} <span className="text-muted-foreground font-normal">· {p.business_name}</span></div>
                        <div className="text-xs text-muted-foreground truncate">{p.email}{p.phone ? ` · ${p.phone}` : ""} · {timeAgo(p.created_at)}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="font-heading text-xl font-extrabold tracking-[0.25em] text-primary select-all" data-testid={`pending-code-${p.email}`}>{p.code}</code>
                        {!p.expired && (
                          <button onClick={() => copyCode(p.code)} className="grid place-items-center h-8 w-8 rounded-lg border border-border hover:bg-secondary" title="Copy code" data-testid={`copy-${p.email}`}>
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                        {!p.expired && p.phone && (
                          <>
                            <a href={waLink(p)} target="_blank" rel="noreferrer" className="grid place-items-center h-8 w-8 rounded-lg border border-border hover:bg-[#25D366]/10 hover:border-[#25D366] text-[#25D366]" title="Send on WhatsApp" data-testid={`whatsapp-${p.email}`}>
                              <MessageCircle className="h-4 w-4" />
                            </a>
                            <a href={smsLink(p)} className="grid place-items-center h-8 w-8 rounded-lg border border-border hover:bg-secondary" title="Send via SMS" data-testid={`sms-${p.email}`}>
                              <Smartphone className="h-4 w-4" />
                            </a>
                          </>
                        )}
                        {!p.expired && (
                          <button onClick={() => approve(p.email, p.name)} className="inline-flex items-center gap-1.5 h-8 rounded-lg bg-[hsl(var(--success))] text-white px-3 text-xs font-semibold hover:opacity-90" title="Approve & create account" data-testid={`approve-${p.email}`}>
                            <CheckCircle2 className="h-4 w-4" /> Approve
                          </button>
                        )}
                        {p.expired && <span className="text-xs font-semibold text-destructive">Expired</span>}
                        <button onClick={() => dismiss(p.email)} className="grid place-items-center h-8 w-8 rounded-lg border border-border hover:bg-secondary hover:text-destructive" title="Dismiss" data-testid={`dismiss-${p.email}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Businesses table */}
            <div className="rounded-xl border border-border bg-card p-5 md:p-6 shadow-sm animate-fade-up">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <h3 className="font-heading text-lg font-semibold">All Businesses <span className="text-muted-foreground text-sm font-normal">· {businesses.length}</span></h3>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button onClick={cleanupTest} data-testid="cleanup-test-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg border border-border px-3 py-2 text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors whitespace-nowrap">
                    <Trash2 className="h-3.5 w-3.5" /> Remove test shops
                  </button>
                  <div className="relative max-w-xs w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input data-testid="founder-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business or owner…" className="pl-9" />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left font-semibold py-3">Business</th>
                    <th className="text-left font-semibold py-3 hidden md:table-cell">Owner</th>
                    <th className="text-left font-semibold py-3 hidden lg:table-cell">Signed up</th>
                    <th className="text-right font-semibold py-3">Products</th>
                    <th className="text-right font-semibold py-3">Invoices</th>
                    <th className="text-right font-semibold py-3 pl-6">Sales</th>
                    <th className="text-left font-semibold py-3 pl-8 hidden sm:table-cell">Last active</th>
                    <th className="text-right font-semibold py-3">Action</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map((b) => (
                      <tr key={b.business_id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors" data-testid={`founder-biz-${b.business_id}`}>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{b.name}</span>
                            {b.is_suspended
                              ? <Badge variant="destructive" className="text-[9px] uppercase">Suspended</Badge>
                              : b.is_open && <Badge variant="secondary" className="text-[9px] uppercase">Open</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{(b.industries || []).join(", ")}</div>
                        </td>
                        <td className="py-3 hidden md:table-cell">
                          <div>{b.owner_name}</div>
                          <div className="text-xs text-muted-foreground">{b.owner_phone || b.owner_email}</div>
                        </td>
                        <td className="py-3 hidden lg:table-cell text-muted-foreground">{formatDate(b.created_at)}</td>
                        <td className="py-3 text-right tabular-nums">{b.products}</td>
                        <td className="py-3 text-right tabular-nums">{b.invoices}</td>
                        <td className="py-3 text-right tabular-nums font-semibold pl-6">{formatINR(b.total_sales, { compact: true })}</td>
                        <td className="py-3 pl-8 hidden sm:table-cell text-muted-foreground">{b.last_activity ? timeAgo(b.last_activity) : "—"}</td>
                        <td className="py-3 text-right">
                          <div className="inline-flex items-center gap-3 justify-end">
                            <button onClick={() => suspendShop(b)} data-testid={`suspend-shop-${b.business_id}`} className={`inline-flex items-center gap-1 text-xs font-semibold hover:underline ${b.is_suspended ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                              {b.is_suspended ? <><PlayCircle className="h-3.5 w-3.5" /> Reactivate</> : <><PauseCircle className="h-3.5 w-3.5" /> Suspend</>}
                            </button>
                            <button onClick={() => removeShop(b)} data-testid={`remove-shop-${b.business_id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-destructive hover:underline">
                              <X className="h-3.5 w-3.5" /> Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Founders credit */}
            <div className="mt-10 mb-4 text-center">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
                <span className="h-px w-8 bg-border" /> Founded by <span className="h-px w-8 bg-border" />
              </div>
              <div className="mt-2 font-heading text-2xl md:text-3xl font-extrabold tracking-tight bg-gradient-to-r from-primary via-purple-500 to-primary bg-clip-text text-transparent">
                Rohit &amp; Sohan
              </div>
              <div className="mt-1 text-xs text-muted-foreground">Building KHEVORA — the operating system for every business.</div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
